// context/StudentAuthContext.jsx
import { createContext, useContext, useState, useEffect, useRef } from "react";
import { studentAuthApiClient } from "../services/studentAuthApiClient";

const StudentAuthContext = createContext({
  student: null,
  isAuthenticated: false,
  authLoading: false,
  studentLogin: async () => {},
  studentLogout: async () => {},
  checkStudentAuth: async () => {},
});

// Helper function to check if student cookies exist
const hasStudentCookies = () => {
  // Check if any student-related cookie exists
  // This is a simple check - cookies may have different names
  const cookies = document.cookie.split(';');
  return cookies.some(cookie => 
    cookie.trim().startsWith('student_access_token=') || 
    cookie.trim().startsWith('student_')
  );
};

// Helper to get cookie value
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const StudentAuthProvider = ({ children }) => {
  const [student, setStudent] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  const isMounted = useRef(true);
  const initialCheckDone = useRef(false);

  useEffect(() => {
    // console.log("🎯 StudentAuthProvider mounted");
    isMounted.current = true;
    
    const checkInitialAuth = async () => {
      // Skip if already checked
      if (initialCheckDone.current) {
        // console.log("⏭️ Initial check already done, skipping");
        return;
      }
      
      // console.log("🔍 Checking initial auth...");
      
      // First, check if we have student cookies
      const hasCookies = hasStudentCookies();
      
      if (!hasCookies) {
        // console.log("🚫 No student cookies found, skipping auth check");
        if (isMounted.current) {
          setStudent(null);
          setAuthLoading(false);
          initialCheckDone.current = true;
        }
        return;
      }
      
      // If we have cookies, check auth
      console.log("🍪 Student cookies found, checking auth...");
      
      try {
        const res = await studentAuthApiClient.get('/api/students/check-auth', {
          _skipRetry: true // Don't retry on 401
        });
        
        console.log("✅ Auth check successful:", res.data.authenticated);
        
        if (isMounted.current && res.data.authenticated) {
          setStudent(res.data.user);
        } else if (isMounted.current) {
          setStudent(null);
        }
      } catch (error) {
        console.log("⚠️ Auth check failed:", error.response?.status);
        
        // Only log non-401 errors
        if (error.response?.status !== 401) {
          console.error("Auth check error:", error);
        }
        
        if (isMounted.current) {
          setStudent(null);
        }
      } finally {
        if (isMounted.current) {
          console.log("🏁 Setting authLoading to false");
          setAuthLoading(false);
          initialCheckDone.current = true;
        }
      }
    };
    
    // Check auth after a short delay to allow cookies to be available
    const timer = setTimeout(() => {
      checkInitialAuth();
    }, 100);
    
    return () => {
      // console.log("🧹 StudentAuthProvider unmounting");
      isMounted.current = false;
      clearTimeout(timer);
    };
  }, []);

  const studentLogin = async (loginData) => {
    try {
      setAuthLoading(true);
      
      // console.log("📤 Attempting login...");
      const response = await studentAuthApiClient.post('/api/students/login', {
        identifier: loginData.emailOrMobile,
        password: loginData.password
      });
      
      // console.log("✅ Student Login successful");
      
      if (isMounted.current && response.data.success) {
        // Check auth immediately to get user data
        const authCheck = await studentAuthApiClient.get('/api/students/check-auth');
        if (authCheck.data.authenticated) {
          setStudent(authCheck.data.user);
          initialCheckDone.current = true; // Mark as checked
        }
        return { success: true, user: authCheck.data.user };
      }
      
      throw new Error('Login failed');
    } catch (error) {
      console.error("❌ Login error:", error);
      throw error;
    } finally {
      if (isMounted.current) {
        setAuthLoading(false);
      }
    }
  };

  // const studentLogout = async () => {
  //   try {
  //     console.log("🚪 Logging out...");
  //     await studentAuthApiClient.post('/api/students/logout');
  //   } catch (error) {
  //     console.error("Logout error:", error);
  //   } finally {
  //     if (isMounted.current) {
  //       setStudent(null);
  //       initialCheckDone.current = false; // Reset for next session
  //     }
  //     console.log("🔄 Redirecting to login page");
  //     window.location.href = '/students-login';
  //   }
  // };

  // context/StudentAuthContext.jsx (Updated logout function)
const studentLogout = async () => {
    try {
        // console.log("🚪 Logging out...");
        
        // Attempt server-side logout
        await studentAuthApiClient.post('/api/students/logout', {}, {
            timeout: 5000, // 5 second timeout
            headers: {
                'X-Logout-Request': 'true'
            }
        });
        
    } catch (error) {
        // Don't throw error - proceed with local cleanup
        console.warn("Logout API failed, proceeding with local cleanup:", error);
        
        // Check if it's a network error vs auth error
        if (error.response) {
            // Server responded with error
            if (error.response.status === 401) {
                console.log("Already logged out on server");
            }
        }
    } finally {
        if (isMounted.current) {
            setStudent(null);
            initialCheckDone.current = false;
            
            // Clear context-specific data
            localStorage.removeItem('student_last_check');
            sessionStorage.removeItem('student_temp');
        }
    }
    
    // Return success indicator - let hook handle navigation
    return { success: true, localOnly: false };
};

  const checkStudentAuth = async () => {
    try {
      const res = await studentAuthApiClient.get('/api/students/check-auth');
      if (isMounted.current && res.data.authenticated) {
        setStudent(res.data.user);
        return { authenticated: true, user: res.data.user };
      } else {
        if (isMounted.current) {
          setStudent(null);
        }
        return { authenticated: false };
      }
    } catch (error) {
      if (isMounted.current) {
        setStudent(null);
      }
      return { authenticated: false };
    }
  };

  const value = {
    student,
    isAuthenticated: !!student,
    authLoading,
    studentLogin,
    studentLogout,
    checkStudentAuth,
  };

  return (
    <StudentAuthContext.Provider value={value}>
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuth = () => {
  const context = useContext(StudentAuthContext);
  if (!context) {
    throw new Error('useStudentAuth must be used within StudentAuthProvider');
  }
  return context;
};